import { useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles saving a finished game result.
 * Multiplayer: POSTs to /save_game_result/, sends websocket progress, then calls onMultiplayerSaved.
 * Solo:        POSTs to /save_game_result_solo/.
 *
 * The score object must include { mae, avg, differences, absoluteErrors, timeHorizon }
 * so that timeHorizon is captured at game-over time, not at save time.
 */
const useSaveGameResult = ({
  isMultiplayer,
  userId,
  roomId,
  username,
  websocket,
  sessionId,
  globalUserId,
  onMultiplayerSaved,
}) => {
  // Keep websocket in a ref so we never have a stale reference in the callback
  const websocketRef = useRef(websocket);
  useEffect(() => { websocketRef.current = websocket; });

  const onMultiplayerSavedRef = useRef(onMultiplayerSaved);
  useEffect(() => { onMultiplayerSavedRef.current = onMultiplayerSaved; });

  const saveGameResult = useCallback((score) => {
    const { mae, avg, differences, absoluteErrors, timeHorizon, clickCount } = score;
    const isInfinite = timeHorizon === null;

    if (isMultiplayer) {
      axios.post(`${backendUrl}/save_game_result/`, {
        user_id: userId,
        room_game_id: roomId,
        game_finished_at: new Date().toISOString(),
        game_mode: isInfinite ? 'infinite' : 'finite',
        time_horizon: isInfinite ? null : timeHorizon,
        click_count: isInfinite ? clickCount : null,
        average_difference: avg,
        mean_absolute_error: mae,
        differences: differences || [],
        absolute_errors: absoluteErrors || [],
      })
        .then(response => {
          onMultiplayerSavedRef.current?.({ gameId: response.data.game.id, timeHorizon });
          const ws = websocketRef.current;
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'progress', username, progress: 100, timeHorizon }));
          }
        })
        .catch(error => console.error('Error saving multiplayer game result:', error));
    } else {
      if (isInfinite) {
        console.log('Solo infinite mode result not saved (no solo endpoint yet)');
        return;
      }
      axios.post(`${backendUrl}/save_game_result_solo/`, {
        global_user_id: globalUserId,
        session_uuid: sessionId,
        game_finished_at: new Date().toISOString(),
        time_horizon: timeHorizon,
        average_difference: avg,
        mean_absolute_error: mae,
      })
        .then(response => console.log('Solo game result saved successfully:', response.data))
        .catch(error => console.error('Error saving solo game result:', error));
    }
  }, [isMultiplayer, userId, roomId, username, sessionId, globalUserId]);

  return { saveGameResult };
};

export default useSaveGameResult;
